import {
    AssetsLoader,
    BlobLoader,
    InsightsLoader,
    LliamLoader,
    MapNotesLoader,
    RmsLoader,
    StyleLoader,
    Config,
    Request,
} from '../loaders';

// default all loaders and prod, just need accountId and getAccessToken
const DEFAULT_LOADERS = {
    loaders: {
        assets: true,
        blob: true,
        insights: true,
        lliam: true,
        mapNotes: true,
        rms: true,
        isMock: false,
    },
    stage: 'prod',
};

/*
 * Turn all mock loaders on, and set file paths to mock-data folder files
 */
const DEFAULT_MOCK_LOADERS = venueId => ({
    loaders: {
        assets: true,
        blob: true,
        insights: true,
        lliam: true,
        mapNotes: true,
        rms: true,
        isMock: true,
    },
    stage: 'prod',
    filePaths: filePathsFromVenueId(venueId),
});

const CONTENT_TYPES = {
    BADGE: 'badge',
    LOGO: 'logo',
    SPRITESHEET: 'spritesheet-mapbox', // hide the fact that there's two kinds of 'spritesheets' in content
    STYLE: 'style',
    THEME: 'theme',
    UI: 'mapbox-ui-svg',
};

let _baseLoader = { accountId: Config.getAccountId };

/*
 * Retrieve the BaseLoader
 * Impure: retrieves global baseLoader
 * @sig baseLoader :: () -> BaseLoader
 */
const baseLoader = () => _baseLoader;

/*
 * Set the file paths based on mock-data
 * @sig filePathsFromVenueId :: VenueId -> Object
 */
const filePathsFromVenueId = venueId => ({
    aiJsonPath: `source-files/${venueId}/${venueId}.v5.json`,
    boundingBoxesPath: `bounds/${venueId}/${venueId}-bounding-boxes.json`,
    boundsPolygonsPath: `bounds/${venueId}/${venueId}-bounds-polygons.json`,
    entitiesPath: `source-files/${venueId}/${venueId}-entities.json`,
    geojsonBasePath: `geojson-golden-masters/${venueId}/`,
    stylePath: `source-files/style.json`,
    themePath: `source-files/theme.json`,
    venuePath: `source-files/${venueId}/${venueId}-venue.json`,
    aiVenuePath: `source-files/${venueId}/${venueId}-ai-venue.json`,
    imdfVenuePath: `source-files/${venueId}/${venueId}-imdf-venue.json`,
    ordinalsPath: `source-files/${venueId}/${venueId}-ordinals.json`,
    structuresPath: `source-files/${venueId}/${venueId}-structures.json`,
    levelsPath: `source-files/${venueId}/${venueId}-levels.json`,
});

/*
 * Initialize the baseLoader used by all the download thunks
 * Impure: stores the baseLoader in a global variable
 *
 * This will combine all the requests in each client into one 'baseLoader' that contains all the requests
 *
 * userConfig = {
 *     accountId     : String,
 *     getAccessToken: Function,
 *     trackJS       : Object,
 * }
 *
 * loaderOptions = {
 *     loaders: {                           // the loaders determine which client requests are added to the baseLoader
 *         assets  : Boolean
 *         blob    : Boolean
 *         mapNotes: Boolean
 *         rms     : Boolean
 *         isMock  : Boolean
 *     },
 *     stage     : String,                  // which stage of the api to call
 *     filePaths : {                        // which filePaths to load from for the mock version of the loader
 *         aiJsonPath         : String
 *         boundingBoxesPath  : String
 *         boundsPolygonsPath : String
 *         entitiesPath       : String
 *         geojsonBasePath    : String
 *         stylePath          : String
 *         themePath          : String
 *         venuePath          : String
 *         aiVenuePath        : String
 *         imdfVenuePath      : String
 *     }
 * }
 * @sig initializeBaseLoader :: (Object, Object) -> ()
 */
const initializeBaseLoader = (
    loaderOptions = DEFAULT_LOADERS,
    userConfig = { accountId: '', getAccessToken: () => '' },
) => {
    const { accountId, getAccessToken, trackJS } = userConfig;
    const { loaders, stage, filePaths } = loaderOptions;

    const { assets, blob, insights, lliam, mapNotes, rms, isMock } = loaders;

    _baseLoader = {
        ..._baseLoader,
        ...AssetsLoader(isMock, assets),
        ...BlobLoader(isMock, blob),
        ...InsightsLoader(isMock, insights),
        ...LliamLoader(isMock, lliam),
        ...MapNotesLoader(isMock, mapNotes),
        ...RmsLoader(isMock, filePaths, rms),
        ...StyleLoader,
    };

    if (trackJS) Request.setTrackJS(trackJS);

    Config.setConfig(stage, accountId, getAccessToken);

    return baseLoader();
};

export { DEFAULT_LOADERS, DEFAULT_MOCK_LOADERS, CONTENT_TYPES, baseLoader, initializeBaseLoader, filePathsFromVenueId };
